<template>
  <section
    class="search-drawer"
    :class="finalProducts.length ? 'full-width' : ''"
    js-overlay="scrollElement"
    :aria-label="$string('predictive_search.accessibility.drawer_aria_label')"
    aria-live="assertive"
  >
    <div class="search-drawer__content-wrapper">
      <button
        class="search-drawer__close icon-button"
        type="button"
        @click.prevent="closeSearch"
      >
        <span
          class="visually-hidden"
          v-text="$string('accessibility.close')"
        />

        <icon-close-outline />
      </button>

      <div class="search-drawer__content">
        <h2
          class="text-heading-content-2xs"
          v-text="$string('predictive_search.title')"
        />

        <!-- Search input box -->
        <div class="search-drawer__input-wrapper">
          <form
            class="search-drawer__form"
            :action="$variable('routes.search')"
            method="get"
            role="search"
            @submit.prevent="submitForm"
          >
            <label
              for="search-drawer-input"
              class="visually-hidden"
              v-text="$string('predictive_search.placeholder')"
            />

            <icon-search v-if="!isMobile" />
            <icon-search-mobile v-if="isMobile" />

            <input
              id="search-drawer-input"
              ref="searchInput"
              type="search"
              :placeholder="$string('predictive_search.placeholder')"
              class="search-drawer__input text-body-2xs-regular"
              autocomplete="off"
              name="q"
              :value="input"
              required
              :aria-describedby="showEmptyResults ? 'search-drawer-empty' : ''"
              @input="updateSearchQuery"
            >

            <button
              v-if="query"
              class="search-drawer__clear icon-button"
              type="button"
              @click="setSearchInput('')"
            >
              <span
                class="visually-hidden"
                v-text="$string('predictive_search.clear')"
              />

              <icon-close />
            </button>
          </form>

          <p
            v-if="showEmptyResults && query"
            id="search-drawer-empty"
            class="search-drawer__no_results text-body-2xs-regular"
            v-text="$string('predictive_search.no_results', { replace: { query } })"
          />
        </div>

        <!-- Trending or suggested terms -->
        <div
          v-if="suggestions.length && showEmptyResults"
          class="search-drawer__suggestions"
        >
          <h3
            class="search-drawer__heading text-body-xs-regular mb-s"
            v-text="showEmptyResults ?
              $string('predictive_search.trending') :
              $string('predictive_search.suggestions')
            "
          />

          <ul>
            <li
              v-for="suggestion of suggestions"
              :key="suggestion"
            >
              <button
                type="button"
                class="search-drawer__suggestion icon-button text-body-2xs-regular"
                :class="{ [$class('loading')]: isLoading }"
                @click="setSearchSuggestion(suggestion)"
                v-text="suggestion"
              />
            </li>
          </ul>
        </div>

        <div
          v-if="finalProducts.length && finalReqs.length && showCollectionResults"
          class="search-drawer__collections-container"
        >
          <p
            v-if="isMobile && showPageAndArticleResults"
            class="text-heading-content-4xs search-drawer__suggestion-heading"
            v-text="$string('search.suggestions_heading_with_pages')"
          />
          <p
            v-if="!isMobile || !showPageAndArticleResults"
            class="text-heading-content-4xs search-drawer__suggestion-heading"
            v-text="$string('search.suggestions_heading')"
          />
          <ul>
            <li
              v-for="value of finalReqs"
              :key="value"
            >
              <a
                class="text-body-2xs-regular search-drawer__suggestion"
                :href="$localizeUrl(getSuggestionsUrl(value))"
                :title="value"
                v-text="value"
              />
            </li>
          </ul>
        </div>

        <!-- content search (articles / pages) results - Desktop view -->
        <div
          v-if="showPageAndArticleResults &&
            (articlesResults.length || pagesResults.length) && !showEmptyResults"
          class="search-drawer__pages-results"
        >
          <div
            v-if="!finalProducts.length && isMobile"
            class="search-drawer__no-collections-title"
          >
            <p
              v-if="isMobile && showPageAndArticleResults"
              class="text-heading-content-4xs search-drawer__suggestion-heading"
              v-text="$string('search.suggestions_heading_with_pages')"
            />
          </div>

          <h3
            v-show="!isMobile"
            class="search-drawer__heading text-body-xs-regular mb-s"
            v-text="$string('predictive_search.content_results')"
          />

          <ul>
            <li
              v-for="combinedResult of combinedResults"
              :key="combinedResult"
            >
              <a
                :href="$localizeUrl(combinedResult.url)"
                class="search-drawer__suggestion icon-button text-body-2xs-regular"
                :class="{ [$class('loading')]: isLoading }"
                v-html="combinedResult.name"
              />
            </li>
          </ul>
        </div>
      </div>

      <!-- Quick Links Menu -->
      <div
        v-if="!finalProducts.length && menu?.items?.length"
        class="search-drawer__menu"
      >
        <h3
          class="search-drawer__heading"
          :class="isMobile ? 'text-body-regular' : 'text-body-xs-regular'"
          v-text="menu.title"
        />

        <ul>
          <li
            v-for="link of menu.items"
            :key="link.title"
          >
            <a
              :href="$localizeUrl(link.url)"
              class="icon-button text-body-2xs-regular"
              v-text="link.title"
            />
          </li>
        </ul>
      </div>
    </div>

    <template v-if="finalProducts.length">
      <div
        class="search-drawer__products"
        :class="{ [$class('loading')]: isLoading }"
      >
        <product-card
          v-for="product of finalProducts"
          :key="product.id"
          :product="product"
          :show-quick-add="false"
          image-is-linked
        />
      </div>

      <btn
        class="search-drawer__cta"
        :label="$string('predictive_search.cta_label', { replace: { query } })"
        modifiers="primary block center"
        :show-icon="false"
        :url="`${$variable('routes.search')}?q=${query}`"
      />
    </template>
  </section>
</template>

<script>

/**
 * Vue: Search drawer (search-drawer)
 * -----------------------------------------------------------------------------
 * A SearchSpring Search drawer.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {Object} overlay - Overlay API.
 * @param {Object} menu - Section's Liquid quick links menu.
 * @param {String} siteId - Searchspring site id.
 * @param {String} articlesSiteId - Searchspring site id.
 * @param {String} pagesSiteId - Searchspring site id.
 * @param {String} showPageAndArticleResults - Whether to show pages/articles
 */
import { mapActions, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import ProductCard from '~global/product-card/product-card'

import IconClose from '~icons/directional-navigation/close.svg'
import IconCloseOutline from '~icons/directional-navigation/close-outline.svg'
import IconSearch from '~icons/general/search.svg'
import IconSearchMobile from '~icons/general/search-mobile.svg'

import localizeUrl from '~/core/helpers/localize-url'
import { debounce, getHighlightedText } from '~/helpers/general'
import { keywordSearch as DYKeywordSearchEvent } from '~/helpers/dynamic-yield'
import { searchspringApiCall } from '~/helpers/searchspring'

export default {
  name: 'SearchDrawer',

  components: {
    Btn,
    IconClose,
    IconCloseOutline,
    IconSearch,
    IconSearchMobile,
    ProductCard,
  },

  props: {
    overlay: {
      type: Object,
      default: () => ({}),
    },
    menu: {
      type: Object,
      default: () => ({}),
    },
    siteId: {
      type: String,
      required: true,
    },
    articlesSiteId: {
      type: String,
      default: '',
    },
    pagesSiteId: {
      type: String,
      default: '',
    },
    showPageAndArticleResults: {
      type: Boolean,
      default: true,
    },
    showCollectionResults: {
      type: Boolean,
      default: true,
    },
    enableSuggestedTermsTargetCollection: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      // Whether search is being performed
      isLoading: true,
      // Whether the first query was performed
      initialQueryPerformed: false,
      // The latest results from SS
      searchspringResults: null,
      // The latest product results from Shopify
      shopifyResults: null,
      // SS Suggested search terms (Either from trending api or suggest api).
      suggestions: [],
      articlesResults: [],
      pagesResults: [],
      // Search query
      query: '',
      // Input value
      input: '',
      // Number of product results to retrieve from SS per page.
      productsCount: 9,
      // Number of article results to retrieve from SS per page.
      articlesCount: 3,
      // Number of page results to retrieve from SS per page.
      pagesCount: 3,
      // SS redirect url
      redirect: '',
      reqs: [],
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      settings: (state) => state.index.settings,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Search endpoint
     * @returns {String} The endpoint
     */
    endpoint() {
      if (this.query) {
        return 'autocomplete'
      }

      return 'trending'
    },

    /**
     * The Shopify products in their final format.
     * @returns {Array} Products
     */
    finalProducts() {
      return this.shopifyResults || []
    },

    /**
     * Show empty results
     * @returns {Boolean} Show empty results
     */
    showEmptyResults() {
      if (!this.query) {
        return true
      }

      if (this.isLoading || !this.query) {
        return false
      }

      return this.finalProducts.length < 1 &&
        this.articlesResults.length < 1 &&
        this.pagesResults.length < 1
    },

    finalReqs() {
      if (!this.enableSuggestedTermsTargetCollection) {
        return []
      }

      return this.reqs.slice(0, 3)
    },

    combinedResults() {
      let combinedResults = [
        ...this.articlesResults,
        ...this.pagesResults,
      ]

      combinedResults = combinedResults.sort(
        (a, b) => new Date(b.published_at) - new Date(a.published_at),
      )

      if (this.isMobile) {
        return combinedResults.slice(0, 1)
      }

      return combinedResults.slice(0, 2)
    },
  },

  watch: {

    /**
     * Watch for ss results
     */
    searchspringResults: {
      handler() {
        const { autocompleteResults, suggestResults, trending } = this.searchspringResults || {}

        // Set SS redirect url.
        this.redirect = autocompleteResults?.merchandising?.redirect || ''

        if (autocompleteResults?.results?.length) {
          const reqs = autocompleteResults?.facets.filter(facet => facet.field === 'tags_category')
          const newReqs = [
            ...Object.entries(reqs?.[0]?.values || []).map((obj) => {
              return obj[1].value
            }),
          ]
          this.reqs = newReqs.length ? newReqs : []
        }

        const queries = trending?.queries || []

        if (queries.length) {
          this.suggestions = queries
            .filter(query => query.searchQuery)
            .map(query => query.searchQuery)
        }

        if (suggestResults) {
          const suggested = suggestResults?.suggested?.text
          const alternatives = suggestResults?.alternatives || []
          const alternativesText = alternatives
            .filter(alternative => alternative.text)
            .map(alternative => alternative.text)

          this.suggestions = suggested ? [suggested, ...alternativesText] : []
        }
      },
      immediate: true,
      deep: true,
    },

    /**
     * Update loading status on input change
     */
    input() {
      this.isLoading = true
      this.redirect = ''
    },

    /**
     * Update results, etc. on query change
     */
    query() {
      if (this.query) {
        return
      }

      this.searchspringResults = null
      this.shopifyResults = null
      this.articlesResults = []
      this.pagesResults = []
    },

    /**
     * Calculate debounced final products
     */
    finalProducts: {
      handler() {
        setTimeout(() => {
          this.overlay.update({ currentElement: this.$refs?.searchInput })
        }, this.settings.throttle)

        if (this.finalProducts?.length) {
          this.overlay?.ref?.classList?.add('full-width')
          return
        }

        this.overlay?.ref?.classList?.remove('full-width')
      },
      immediate: true,
      deep: true,
    },
  },

  created() {
    this.debouncedUpdateSearchQuery = debounce(this.setQuery, this.settings.throttle)
  },

  mounted() {
    this.overlay?.addCallback(this.closeSearch, 'esc')

    this.performSSTrendingSearch()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Update search query
     * @param {Event} event - Search event
     */
    updateSearchQuery(event) {
      const inputValue = event?.target?.value
      this.setSearchInput(inputValue)
    },

    /**
     * Get suggestions url
     * @param {Object} path - url path
     * @returns {String} The suggestions url
     */
    getSuggestionsUrl(path) {
      const targetCollection = this.$variable('settings.search.suggestionsTermsTargetCollection')
      return `${cnvs.routes.normalisedRoot}collections/${targetCollection}?filter.tags_category=${path}`
    },

    /**
     * Sets search input and clears searchspringResults and shopifyResults.
     * @param {String} value - new query value
     */
    setSearchInput(value) {
      this.input = value
      this.overlay.update({ currentElement: this.$refs?.searchInput })

      this.debouncedUpdateSearchQuery(value)
    },

    /**
     * Redirects to search suggestion's results page
     * @param {String} value - new query value
     */
    setSearchSuggestion(value) {
      window.location = this.redirect
        ? localizeUrl(this.redirect)
        : `${this.$variable('routes.search')}?q=${encodeURIComponent(value)}`
    },

    /**
     * Sets query data property and performs search unless blank.
     * @param {String} value - new query value
     */
    setQuery(value) {
      this.query = value

      this.performSSProductSearch()
      this.performSSArticleSearch()
      this.performSSPageSearch()
      DYKeywordSearchEvent(value)
    },

    /**
     * Sets SS and Shopify data in the component when SS updates.
     * @param {Object} results - The SS query results
     */
    searchspringUpdated(results) {
      switch (results.searchType) {
        case 'trending':
          this.trendingAPIHandler(results)
          break
        case 'autocomplete':
          this.autocompleteAPIHandler(results)
          break
      }
    },

    /**
     * Handle API update for trending
     * @param {Object} results - Results data
     */
    trendingAPIHandler(results) {
      this.searchspringResults = results?.searchspring
      this.isLoading = false
    },

    /**
     * Handle API update for autocomplete
     * @param {Object} results - Results data
     */
    autocompleteAPIHandler(results) {
      // Set all facet data if requested
      if (!this.initialQueryPerformed) {
        this.allFacets = results?.searchspring?.facets
      }

      if (this.query) {
        this.initialQueryPerformed = true
        this.searchspringResults = results?.searchspring
        this.shopifyResults = results?.shopify
        this.isLoading = false
      } else {
        this.searchspringResults = null
        this.shopifyResults = null
        this.articlesResults = []
        this.pagesResults = []
      }
    },

    async performSSTrendingSearch() {
      const data = await searchspringApiCall({
        endpoint: 'trending',
        query: '',
        getShopifyProducts: false,
        redirectResponse: 'full',
        resultsPerPage: null,
        siteId: this.siteId,
      })

      this.searchspringUpdated(data)
    },

    /**
     * Fetches product results from SS based on query
     */
    async performSSProductSearch() {
      const data = await searchspringApiCall({
        endpoint: this.endpoint,
        query: this.query,
        getShopifyProducts: true,
        redirectResponse: 'full',
        resultsPerPage: this.productsCount,
        siteId: this.siteId,
      })

      this.searchspringUpdated(data)
    },

    /**
     * Fetches article results from SS based on query
     */
    async performSSArticleSearch() {
      if (!this.showPageAndArticleResults) {
        return
      }

      const data = await searchspringApiCall({
        endpoint: 'autocomplete',
        query: this.query,
        isProducts: false,
        redirectResponse: 'full',
        resultsPerPage: this.articlesCount,
        siteId: this.articlesSiteId,
      })

      this.searchspringArticlesUpdated(data)
    },

    /**
     * Fetches page results from SS based on query
     */
    async performSSPageSearch() {
      if (!this.showPageAndArticleResults) {
        return
      }

      const data = await searchspringApiCall({
        endpoint: 'autocomplete',
        query: this.query,
        filters: ['filter.visible=1'],
        isProducts: false,
        redirectResponse: 'full',
        resultsPerPage: this.pagesCount,
        siteId: this.pagesSiteId,
      })

      this.searchspringPagesUpdated(data)
    },

    /**
     * Sets SS articles results.
     * @param {Object} results - The SS query results
     */
    searchspringArticlesUpdated(results) {
      if (!results?.searchspring?.autocompleteResults?.results) {
        return
      }

      // eslint-disable-next-line no-unused-vars
      const articles = results.searchspring.autocompleteResults.results
        .filter(result => result.name && result.url)
        .map(result => {
          return {
            ...result,
            name: getHighlightedText(result.name, this.query),
          }
        })

      this.articlesResults = articles
    },

    /**
     * Sets SS pages results.
     * @param {Object} results - The SS query results
     */
    searchspringPagesUpdated(results) {
      if (!results?.searchspring?.autocompleteResults?.results) {
        return
      }

      // eslint-disable-next-line no-unused-vars
      const pages = results.searchspring.autocompleteResults.results
        .filter(result => result.name && result.url)
        .map(result => {
          return {
            ...result,
            name: getHighlightedText(result.name, this.query),
          }
        })

      this.pagesResults = pages
    },

    /**
     * Form submit event handler.
     */
    submitForm() {
      window.location = this.redirect
        ? localizeUrl(this.redirect)
        : `${this.$variable('routes.search')}?q=${encodeURIComponent(this.input)}`
    },

    /**
     * Search close event handler
     */
    closeSearch() {
      this.input = ''
      this.setSearchInput('')
      this.suggestions = []
      this.closeOverlay('searchDrawer')
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      this.openOverlay({
        component: 'search-drawer',
        ignoreDismissed: true,
        namespace: 'searchDrawer',
      })
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.closeOverlay('searchDrawer')
    },
  },
}
</script>

<style lang="scss">
@import './search-drawer';
</style>
